<script setup lang="ts">
  import { computed, inject, nextTick, onBeforeMount, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';

  import type { Email, EmailThread, Partner, Task } from '@stuut/api/models';
  import { indexOf } from '@stuut/common/src/utils';
  import { Alert, AlertDescription, AlertTitle } from '@ui/components/alert';
  import { Button } from '@ui/components/button';
  import { Sheet, SheetClose, SheetContent, SheetHeader } from '@ui/components/sheet';
  import { toast } from '@ui/components/sonner';
  import { cn } from '@ui/utils/utils.ts';

  import { stuut } from '@/api';
  import EmailRelatedTasksPanel from '@/components/email-threads/EmailRelatedTasksPanel.vue';
  import EmailThreadEmailList from '@/components/email-threads/EmailThreadEmailList.vue';
  import EmailThreadSidebar from '@/components/email-threads/EmailThreadSidebar.vue';
  import InboxOutreachHeader from '@/components/inbox/InboxOutreachHeader.vue';
  import { useEmailProcessingState } from '@/composables/useEmailProcessingState';
  import { sdk } from '@/sdk';
  import { useTaskCollectionStore } from '@/store';

  const props = defineProps<Props>();

  const emits = defineEmits<EmailThreadEvents>();

  interface Props {
    isSelected?: boolean;
    optionalReply?: boolean;
    selectedEmailId?: string;
    thread: EmailThread;
  }

  export interface EmailThreadEvents {
    (e: 'deleted'): void;

    (e: 'read'): void;

    (e: 'updated', emailThread: EmailThread): void;

    (e: 'reopened', emailThread: EmailThread): void;

    (e: 'close'): void;
  }

  const taskCollectionStore = useTaskCollectionStore();
  const closeDrawer = inject<() => void>('closeDrawer');
  const route = useRoute();

  const isExpanded = ref(false);
  const toggleFullscreen = () => {
    isExpanded.value = !isExpanded.value;
  };

  const emailThread = ref<EmailThread>(props.thread);
  const { processingStep, isProcessing, processingLabel } = useEmailProcessingState(emailThread);

  const partner = ref<Partner>();
  const scrollContainerRef = ref<HTMLElement>();

  const scrollToEmail = async (emailId: string) => {
    await nextTick();
    setTimeout(() => {
      const el = scrollContainerRef.value?.querySelector<HTMLElement>(`#${CSS.escape(emailId)}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const onDraftCreated = (email: Email) => {
    if (!emailThread.value) return;

    emailThread.value.emails.push(email);
    emits('updated', emailThread.value);
    scrollToEmail(email.id);
  };

  const handleDraftReplaced = (email: Email) => {
    scrollToEmail(email.id);
  };

  const triggerOptionalReply = async () => {
    if (!props.optionalReply || !props.selectedEmailId) return;

    const targetEmail = emailThread.value.emails.find((em) => em.id === props.selectedEmailId);

    if (!targetEmail) return;

    const existingDraft = emailThread.value.emails.find(
      (em) => !em.isSent && em.parent_email_id === targetEmail.id,
    );

    if (existingDraft) {
      scrollToEmail(existingDraft.id);
      return;
    }

    try {
      const draftEmail = await stuut.emails.create({
        options: { reply_all: false },
        parent_email: targetEmail.id,
      });
      onDraftCreated(draftEmail);
      toast.success('Draft reply has been created.');
    } catch (err: any) {
      toast.error(err.clientMessage || 'Failed to create draft reply.');
    }
  };

  const onEmailDeleted = (deletedEmail: Email) => {
    const idx = indexOf(emailThread.value.emails, (email) => email.id === deletedEmail.id);

    if (idx !== -1) emailThread.value.emails.splice(idx, 1);
    if (emailThread.value.emails.length === 0) {
      // only emit deleted which calls onThreadDeleted when there are no emails left in thread
      emits('deleted');
    }
  };

  // The thread carries a lightweight partner; the full one is fetched separately. Only show the
  // header skeleton while that fetch is pending — a partnerless (e.g. internal) thread isn't loading.
  const isPartnerLoading = computed(() => !!emailThread.value?.partner && !partner.value);

  const fetchThreadData = async () => {
    try {
      await stuut.emailThreads.retrieve(props.thread.id, emailThread.value);

      if (emailThread.value.partner) {
        partner.value = await stuut.partners.retrieve(
          emailThread.value.partner.id,
          emailThread.value.partner,
        );
      }

      emits('read');

      await sdk.emailThreads.markAsRead({ emailThreadId: emailThread.value.id });

      if (props.optionalReply) {
        await nextTick();
        await triggerOptionalReply();
      }
    } catch (err) {
      console.error(err);
    }
  };

  onBeforeMount(fetchThreadData);

  watch(
    () => props.thread,
    (newThread) => {
      emailThread.value = newThread;
    },
  );

  const beforeTaskOpen = () => {
    closeDrawer?.();
    emits('close');
  };

  const handleOpenTask = (task: Task) => {
    beforeTaskOpen();

    nextTick(() =>
      setTimeout(() => taskCollectionStore.openTaskByQuery?.(task, route.name as string), 0),
    );
  };

  const hasTasks = computed(() => !!emailThread.value?.tasks?.length);
  const hasDocuments = computed(
    () => !!emailThread.value?.invoices?.length || !!emailThread.value?.credit_memos?.length,
  );
  const showSidebar = computed(() => hasTasks.value || hasDocuments.value);

  const newEmailIds = ref<Set<string>>(new Set());

  const refetchAndHighlightNewEmails = async () => {
    const knownIds = new Set(emailThread.value.emails.map((e) => e.id));
    try {
      await stuut.emailThreads.retrieve(emailThread.value.id, emailThread.value);
    } catch (err) {
      console.error('[EmailThreadDrawer] Failed to refetch thread', err);
      return;
    }
    const addedIds: string[] = [];
    for (const email of emailThread.value.emails) {
      if (!knownIds.has(email.id)) {
        newEmailIds.value.add(email.id);
        addedIds.push(email.id);
        setTimeout(() => newEmailIds.value.delete(email.id), 4500);
      }
    }
    if (addedIds.length) {
      const lastAdded = addedIds[addedIds.length - 1];
      setTimeout(() => {
        document.getElementById(lastAdded)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  watch(processingStep, (step) => {
    if (step === 'processing' || step === 'creating_tasks') {
      refetchAndHighlightNewEmails();
    }
  });

  const isDismissed = computed(
    () =>
      emailThread.value.is_from_centralized_inbox &&
      emailThread.value.triage?.status === 'dismissed',
  );

  const DISMISSED_LABEL_MAP: Record<string, string> = {
    NOT_RELEVANT: 'Not Relevant',
    SPAM: 'Spam',
  };

  const dismissedLabel = computed(() => {
    const reason = emailThread.value.triage?.agent_confidence_reason;
    if (!reason) return { label: 'Dismissed', reason: '' };
    const match = reason.match(/^\[(\w+)]\s*(.*)/);
    if (match) return { label: DISMISSED_LABEL_MAP[match[1]] ?? match[1], reason: match[2] };
    return { label: 'Dismissed', reason };
  });

  const isReopening = ref(false);

  const handleReopen = async () => {
    isReopening.value = true;
    try {
      await sdk.emailThreads.reopen({ emailThreadId: emailThread.value.id });
      const updated = await stuut.emailThreads.retrieve(emailThread.value.id);
      emailThread.value = updated;
      emits('reopened', updated);
      toast.success('Thread reopened for triage.');
    } catch (error: any) {
      console.error('Failed to reopen thread:', error);
      toast.error(error.clientMessage || 'Failed to reopen thread.');
    } finally {
      isReopening.value = false;
    }
  };
</script>

<template>
  <Sheet v-bind="$attrs">
    <SheetContent
      :class="
        cn(
          'gap-y-0',
          isExpanded
            ? 'w-full'
            : showSidebar
              ? 'w-[90vw] xl:w-[80vw]! xl:max-w-[1600px]'
              : 'lg:w-[60vw]!',
        )
      "
      side="right"
      floating
      close-position="right"
      :hide-close-btn="true"
      style="width: calc(100% - 1rem)"
      @close="emits('close')"
      @keyup.escape="emits('close')"
    >
      <SheetHeader class="border-b flex-row items-center gap-2">
        <div class="flex-1 min-w-0">
          <Alert v-if="isDismissed" variant="primary" size="xs" icon="far fa-exclamation-circle">
            <div class="flex items-center w-full justify-between">
              <div class="flex flex-col gap-y-1">
                <AlertTitle>Email marked as {{ dismissedLabel.label }}</AlertTitle>
                <AlertDescription v-if="dismissedLabel.reason">
                  {{ dismissedLabel.reason }}
                </AlertDescription>
              </div>

              <Button variant="outline" size="sm" :loading="isReopening" @click="handleReopen">
                Move to inbox
              </Button>
            </div>
          </Alert>

          <InboxOutreachHeader
            v-else
            :partner="partner"
            :loading="isPartnerLoading"
            :invoices="emailThread?.invoices"
            :credit-memos="emailThread?.credit_memos"
            :processing-step="processingStep"
            :is-internal="emailThread?.is_internal"
          />
        </div>

        <div class="flex items-center shrink-0">
          <Button variant="ghost" size="icon-xs" @click="toggleFullscreen">
            <i class="fas" :class="isExpanded ? 'fa-compress' : 'fa-expand'" />
          </Button>

          <SheetClose as-child>
            <Button variant="ghost" size="icon-xs">
              <i class="fal fa-close text-base!" />
            </Button>
          </SheetClose>
        </div>
      </SheetHeader>

      <div
        ref="scrollContainerRef"
        :class="
          cn(
            'w-full flex-1 p-2 min-h-0 overflow-y-auto gap-2 grid bg-muted',
            showSidebar ? 'lg:grid-cols-[1fr_auto]' : 'grid-cols-[1fr]',
          )
        "
      >
        <EmailThreadEmailList
          class="bg-muted pb-15"
          :thread="emailThread"
          size="sm"
          :selected-email-id="selectedEmailId"
          :processing="isProcessing"
          :new-email-ids="newEmailIds"
          @draft-created="onDraftCreated"
          @updated="emits('updated', emailThread)"
          @deleted="onEmailDeleted"
        >
          <template #after_body="{ email, emails, isCollapsed, processing }">
            <EmailRelatedTasksPanel
              v-if="!isCollapsed && email.openTasks?.length"
              :email="email"
              :thread-emails="emails"
              :is-processing="processing"
              :processing-label="processingLabel"
              @draft-created="onDraftCreated"
              @draft-replaced="handleDraftReplaced"
              @open-task="(task) => handleOpenTask(task)"
              @before-task-open="beforeTaskOpen"
            />
          </template>
        </EmailThreadEmailList>

        <EmailThreadSidebar
          v-if="showSidebar"
          class="flex justify-end rounded-md h-full"
          :email-thread="emailThread"
          :partner="partner"
          @open-task="handleOpenTask"
          @before-task-open="beforeTaskOpen"
        />
      </div>
    </SheetContent>
  </Sheet>
</template>

<style lang="postcss" scoped>
  @keyframes new-email-fade {
    from {
      background-color: var(--warning-muted);
    }
    to {
      background-color: var(--color-card);
    }
  }

  .animate-new-email {
    animation: new-email-fade 1500ms ease-out forwards;
  }
</style>
